import React, { memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { JsonPreviewModal } from 'src/components/payments-ops/JsonPreviewModal'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { AuthorizeTransactionModal } from 'src/components/payments-ops/transactions/AuthorizeTransactionModal'
import { OutgoingTransactionsTable } from 'src/components/payments-ops/transactions/OutgoingTransactionsTable'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { AdminFetchPendingReviewTransactions } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import type { PaymentsOpsTransaction } from 'src/interfaces/transactionsReview'
import {
  getOutgoingTransactions,
  getOutgoingTransactionsError,
} from 'src/selectors/transactionsReview'
import { addS } from 'src/utils/baseUtils'
import { pickTransactionDetails } from 'src/utils/paymentsOps/transactionDetails'

const OutgoingTransactionsPageComponent = () => {
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const rows = useSelector(getOutgoingTransactions)
  const listError = useSelector(getOutgoingTransactionsError)

  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [detailsTransaction, setDetailsTransaction] = useState<PaymentsOpsTransaction | null>(null)
  const [authorizingTransaction, setAuthorizingTransaction] =
    useState<PaymentsOpsTransaction | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchPendingReviewTransactions.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken])

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handleCloseDetails = useCallback(() => {
    setDetailsTransaction(null)
  }, [])

  const handleCloseAuthorize = useCallback(() => {
    setAuthorizingTransaction(null)
  }, [])

  return (
    <div
      data-testid="outgoing-transactions-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="Outgoing Transactions"
        description={`${rows.length} ${addS(rows.length, 'transaction')} awaiting authorization`}
      />
      <ToolbarRow>
        <div css={tw`ml-auto pb-1`}>
          <Button
            size="small"
            color="white"
            leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </div>
      </ToolbarRow>
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <OutgoingTransactionsTable
        onViewDetails={setDetailsTransaction}
        onAuthorize={setAuthorizingTransaction}
      />
      <JsonPreviewModal
        title="Transaction details"
        value={detailsTransaction ? pickTransactionDetails(detailsTransaction) : null}
        onClose={handleCloseDetails}
      />
      <AuthorizeTransactionModal
        transaction={authorizingTransaction}
        onClose={handleCloseAuthorize}
      />
    </div>
  )
}

export const OutgoingTransactionsPage = memo(OutgoingTransactionsPageComponent)
